import { useState, useCallback, useMemo } from 'react';
import { useInfiniteQuery, QueryKey, InfiniteData } from '@tanstack/react-query';
import { useAuth } from '../contexts/auth-context';
import {
  Attachment
} from '../types/message-types';
import { supabase } from '../lib/supabase-client';

// Raw row from the chat_messages table
interface ChatMessageRow {
  id: string;
  session_id: string | null;
  role: string | null;        // 'user' | 'assistant' | 'system' | provider name
  content: string | null;
  metadata: Record<string, unknown> | null;
  timestamp: string | null;
}

/**
 * MessageData is the shape the rest of the app consumes.
 *
 * chat_messages lacks many fields expected by the original messages table
 * (sender_name, recipient_name, subject, read, attachments, etc.). We derive
 * or default them from the columns that DO exist so downstream components
 * continue to work without breaking changes.
 */
export interface MessageData {
  id: string;
  sender_id: string;
  sender_name: string;
  sender_role: string;
  recipient_id: string;
  recipient_name: string;
  recipient_role: string;
  subject: string | null;
  content: string;
  timestamp: string;
  read: boolean;
  attachments: Attachment[] | null;
  created_at: string;
  updated_at: string;
}

export interface MessagesFilters {
  searchTerm?: string;
  unreadOnly?: boolean;
  urgentOnly?: boolean;
  providerId?: string;
}

interface ApiErrorResponse {
  message: string;
  statusCode?: number;
  [key: string]: unknown;
}

const MESSAGE_QUERY_KEY = 'messages';
const PAGE_SIZE = 15;

export const messageKeys = {
  all: [MESSAGE_QUERY_KEY] as const,
  lists: () => [...messageKeys.all, 'list'] as const,
  list: (filters: MessagesFilters = {}) => [...messageKeys.lists(), filters] as const,
  details: () => [...messageKeys.all, 'detail'] as const,
  detail: (id: string | undefined) => [...messageKeys.details(), id] as const,
};

/**
 * Maps a raw chat_messages row to the MessageData shape.
 *
 * Fields that don't exist in chat_messages are derived from what is available:
 * - sender_name: from metadata.sender_name, or the role field, or 'Unknown'
 * - sender_id / recipient_id: from metadata if present, otherwise empty string
 * - subject: from metadata.subject if present, otherwise null
 * - read: defaults to false (chat_messages has no read flag)
 * - attachments: from metadata.attachments if present, otherwise null
 */
const mapChatMessageRow = (row: ChatMessageRow): MessageData => {
  const meta = row.metadata ?? {};

  const senderName =
    (meta['sender_name'] as string | undefined) ??
    row.role ??
    'Unknown';

  const senderId = (meta['sender_id'] as string | undefined) ?? '';
  const recipientId = (meta['recipient_id'] as string | undefined) ?? '';
  const recipientName = (meta['recipient_name'] as string | undefined) ?? '';
  const subject = (meta['subject'] as string | undefined) ?? null;
  const attachments = (meta['attachments'] as Attachment[] | undefined) ?? null;
  const ts = row.timestamp ?? new Date(0).toISOString();

  return {
    id: row.id,
    sender_id: senderId,
    sender_name: senderName,
    sender_role: row.role ?? '',
    recipient_id: recipientId,
    recipient_name: recipientName,
    recipient_role: (meta['recipient_role'] as string | undefined) ?? '',
    subject,
    content: row.content ?? '',
    timestamp: ts,
    read: false, // chat_messages has no read flag — default to unread
    attachments,
    created_at: ts,
    updated_at: ts,
  };
};

/**
 * Custom hook to manage messaging functionality.
 * Reads from the chat_messages table.
 */
const useMessages = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<MessagesFilters>({
    searchTerm: '',
    unreadOnly: false,
    urgentOnly: false,
    providerId: undefined,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch: refetchConversations,
  } = useInfiniteQuery<
    { data: MessageData[]; nextPage: number | undefined; count: number },
    ApiErrorResponse,
    InfiniteData<{ data: MessageData[]; nextPage: number | undefined; count: number }>,
    QueryKey,
    number
  >({
    queryKey: messageKeys.list(filters),
    queryFn: async ({ pageParam = 1 }) => {
      const from = (pageParam - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('chat_messages')
        .select('*', { count: 'exact' });

      // chat_messages has no read or subject columns; search by content instead
      if (filters.searchTerm) {
        query = query.ilike('content', `%${filters.searchTerm}%`);
      }

      // Filter by session_id when a providerId is supplied (session scoping)
      if (filters.providerId) {
        query = query.eq('session_id', filters.providerId);
      }

      query = query.range(from, to).order('timestamp', { ascending: false });

      const { data: rows, error: queryError, count } = await query;

      if (queryError) {
        console.error('Supabase query error:', queryError);
        throw { message: queryError.message || 'Failed to fetch messages' };
      }

      const mapped = (rows ?? []).map((row: any) =>
        mapChatMessageRow(row as ChatMessageRow)
      );

      // Apply unreadOnly filter client-side (no read column in DB)
      const filtered = filters.unreadOnly ? mapped.filter((m: MessageData) => !m.read) : mapped;

      const totalPages = Math.ceil((count ?? 0) / PAGE_SIZE);
      const nextPage = pageParam < totalPages ? pageParam + 1 : undefined;

      return {
        data: filtered,
        nextPage,
        count: count ?? 0,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 60 * 1000,
    enabled: !!user?.id,
  });

  const messages = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  );

  const updateFilters = useCallback((newFilters: Partial<MessagesFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      searchTerm: '',
      unreadOnly: false,
      urgentOnly: false,
      providerId: undefined,
    });
  }, []);

  return {
    messages,
    filters,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: error ? ((error as ApiErrorResponse).message || 'Failed to load messages') : null,
    updateFilters,
    resetFilters,
    refetchMessages: refetchConversations,
  };
};

export default useMessages;
